/**
 * Settings — page (CES standard)
 * Outpost registry maintenance: which Outposts this tool manages. Each
 * registration points at the owner account and anchor region; the backend
 * validates it against AWS (assume role + GetOutpost) before storing.
 */

import { useState, type FormEvent } from 'react';
import { Button } from '../components/atoms/Button';
import { Input } from '../components/atoms/Input';
import { DataTable, type DataTableColumn } from '../components/molecules/DataTable';
import { useRegistry } from '../hooks/useRegistry';
import type { OutpostRegistration } from '../types/outposts';

const FIELD_CLASSES = 'flex flex-col gap-1 text-xs text-gray-500';

function RemoveButton({
  outpostId,
  onRemove,
}: {
  outpostId: string;
  onRemove: (outpostId: string) => Promise<string | null>;
}) {
  const [confirming, setConfirming] = useState(false);
  const [pending, setPending] = useState(false);

  if (!confirming) {
    return (
      <Button
        variant="secondary"
        small
        data-testid={`remove-${outpostId}`}
        onClick={() => setConfirming(true)}
      >
        Remove
      </Button>
    );
  }
  return (
    <span className="flex items-center gap-2">
      <Button
        variant="danger"
        small
        data-testid={`confirm-remove-${outpostId}`}
        disabled={pending}
        onClick={async () => {
          setPending(true);
          await onRemove(outpostId);
          setPending(false);
        }}
      >
        {pending ? 'Removing…' : 'Confirm remove'}
      </Button>
      <Button variant="secondary" small disabled={pending} onClick={() => setConfirming(false)}>
        Keep
      </Button>
    </span>
  );
}

export function Settings() {
  const { registrations, loading, error, add, remove } = useRegistry();

  const [outpostId, setOutpostId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [region, setRegion] = useState('');
  const [roleArn, setRoleArn] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const columns: DataTableColumn<OutpostRegistration>[] = [
    {
      key: 'outpostId',
      header: 'Outpost ID',
      render: (row) => <span className="font-mono text-xs">{row.outpostId}</span>,
    },
    { key: 'name', header: 'Name' },
    { key: 'accountId', header: 'Owner account' },
    { key: 'region', header: 'Region' },
    {
      key: 'registeredAt',
      header: 'Registered',
      render: (row) => new Date(row.registeredAt).toLocaleString(),
    },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <RemoveButton
          outpostId={row.outpostId}
          onRemove={async (id) => {
            const message = await remove(id);
            setRemoveError(message);
            return message;
          }}
        />
      ),
    },
  ];

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);
    const message = await add({
      outpostId: outpostId.trim(),
      accountId: accountId.trim(),
      region: region.trim(),
      ...(roleArn.trim() ? { roleArn: roleArn.trim() } : {}),
    });
    setSubmitting(false);
    if (message) {
      setFormError(message);
      return;
    }
    setOutpostId('');
    setAccountId('');
    setRegion('');
    setRoleArn('');
  };

  return (
    <>
      <h2 className="mb-1 text-2xl font-bold text-gray-900">Outpost registry</h2>
      <p className="mb-6 text-sm text-gray-500">
        Outposts managed by this module. Each registration is validated against the owner
        account before it is saved.
      </p>

      <section
        aria-label="Register an Outpost"
        className="mb-6 rounded-lg border border-gray-200 bg-white p-5 shadow"
      >
        <h3 className="mb-1 text-base font-bold text-gray-900">Register an Outpost</h3>
        <p className="mb-4 text-sm text-gray-600">
          The tool assumes the read role in the owner account (the org-wide role template,
          or an explicit role ARN below) and verifies the Outpost exists in that account and
          region. Removing a registration only detaches it from this tool.
        </p>

        <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => void submit(e)}>
          <label className={FIELD_CLASSES}>
            Outpost ID
            <Input
              required
              placeholder="op-0123456789abcdef0"
              className="w-56 font-mono"
              data-testid="reg-outpost-id"
              value={outpostId}
              onChange={(e) => setOutpostId(e.target.value)}
            />
          </label>
          <label className={FIELD_CLASSES}>
            Owner account ID
            <Input
              required
              placeholder="123456789012"
              className="w-40"
              data-testid="reg-account-id"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
            />
          </label>
          <label className={FIELD_CLASSES}>
            Anchor region
            <Input
              required
              placeholder="us-east-1"
              className="w-32"
              data-testid="reg-region"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
            />
          </label>
          <label className={FIELD_CLASSES}>
            Role ARN override (optional)
            <Input
              placeholder="arn:aws:iam::…:role/…"
              className="w-72 font-mono"
              data-testid="reg-role-arn"
              value={roleArn}
              onChange={(e) => setRoleArn(e.target.value)}
            />
          </label>
          <Button type="submit" data-testid="register-btn" disabled={submitting}>
            {submitting ? 'Validating…' : 'Register'}
          </Button>
        </form>

        {formError && (
          <p
            className="mt-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
            data-testid="register-error"
            role="alert"
          >
            {formError}
          </p>
        )}
      </section>

      {error && <p className="py-8 text-center text-red-600">{error}</p>}
      {removeError && (
        <p className="mb-3 text-sm text-red-600" data-testid="remove-error">
          {removeError}
        </p>
      )}

      {loading ? (
        <p className="py-8 text-center text-gray-500">Loading registry…</p>
      ) : (
        <section aria-label="Registered Outposts" data-testid="registry-table">
          <DataTable
            data={registrations}
            columns={columns}
            emptyMessage="No Outposts registered. The tool only manages registered Outposts: the inventory stays empty until you register one here."
            rowTestId={(row) => `registration-${row.outpostId}`}
          />
        </section>
      )}
    </>
  );
}
